import {Inject, ModelEvents, Singleton,} from '@denishussar/html5-game-engine';
import {GameModel, GameStateEnum} from '../../Models/GameModel';
import {
    GameEvent,
    GameEventAdAbort,
    GameEventAdComplete,
    GameEventAdStart,
    GameEventBoosterUse,
    GameEventDelay,
    GameEventLevelAbort,
    GameEventLevelComplete,
    GameEventPopupButtonClick,
    GameEventPreloaderAbortCause,
    GameEventSceneName,
    GameEventSceneOpen,
    GameEventStoreButtonClick,
    GameEventStoreExit,
    GameEventStoreOpen,
    GameEventTutorialBoosterUse,
    GameEventTutorialComplete,
    GameEventTutorialPopupOpen,
} from '../../Constants/GameEvent';
import {AmplitudeAnalyticsService} from './AmplitudeAnalyticsController';
import {GoogleAnalyticsService} from './GoogleAnalyticsController';
import {SavesManager} from '../../Managers/SavesManager';
import {ModalsManager} from '../../Common/MadalsManager/ModalsManager';
import {isWebGLSupport} from '../../Utils/Helpers';
import {AnimationContents} from "../../../AnimationContents";
import {GameLogic} from "../../Logic/GameLogic";

export interface ILevelPlayData {
    levelName: string,
    levelSize: number,
    boosters: {
        needle: number,
        yarn: number,
        bucket: number,
    },
    percentage: number,
    // stats
    stitchCount: number,
    dragCount: number,
    needleCount: number,
    yarnCount: number,
    zoomInCount: number,
    zoomOutCount: number,
    storeCount: number,
    timeSpent: number;
    tagType?: string;
    rewardsCount: number;
}

@Singleton
export class AnalyticsController {
    $model: GameModel;
    lastSendTimeStamp: number;

    adStartTimeStamp: number = 0;

    private eventStamps: Map<string, number> = new Map();

    private get modalsManager(): ModalsManager {
        return this.gameLogic.modalsManager;
    }

    constructor(
        @Inject private model: GameModel,
        @Inject private modelEvents: ModelEvents,
        @Inject private amplitudeAnalyticsService: AmplitudeAnalyticsService,
        @Inject private googleAnalyticsService: GoogleAnalyticsService,
        @Inject private saveManager: SavesManager,
        @Inject private gameLogic: GameLogic,
    ) {
        console.log('AnalyticsService::super()', this.model);
        const startTime = localStorage.getItem('lifetime');
        if (!startTime) {
            localStorage.setItem('lifetime', '0');
        }

        setInterval(() => {
            localStorage.setItem(
                'lifetime', String(Number(localStorage.getItem('lifetime')) + 1)
            );
        }, 60000);

        this.modelEvents.on(this.model, GameEvent.preloaderStart, () => {
            this.sendEvent(GameEvent.preloaderStart);
        }, this);

        this.modelEvents.on(this.model, GameEvent.preloaderAbort, ({ cause }) => {
            this.sendEvent(GameEvent.preloaderAbort, { cause, delay: GameEvent.preloaderStart });
        }, this);

        this.modelEvents.on(this.model, GameEvent.preloaderComplete, () => {
            window.removeEventListener('beforeunload', this.onBeforeUnload);

            this.sendEventWithTime(
                GameEvent.preloaderComplete,
                {
                    delay: GameEvent.preloaderStart,
                },
            );
        }, this);

        this.modelEvents.on(this.model, GameEvent.sceneOpen, () => {
            const sceneName = this.getSceneName();
            const levelName = sceneName === GameEventSceneName.gameplay || sceneName === GameEventSceneName.endScreen ? this.model.picture.name : undefined;

            // if (levelName === 'FTUE') {
            //     return;
            // }

            const props = <GameEventSceneOpen & GameEventDelay>{ sceneName };

            if (levelName) {
                props.levelName = levelName;
            }

            props.delay = GameEvent.sceneOpen;

            this.sendEventWithTime(GameEvent.sceneOpen, props);
        }, this);

        this.modelEvents.on(this.model, GameEvent.popupOpen, ({ popupName }) => {
            if (!popupName) {
                popupName = this.modalsManager.getCurrentKey();
            }
            this.sendEventWithTime(GameEvent.popupOpen, <GameEventTutorialPopupOpen>{
                sceneName: this.getSceneName(),
                popupName,
                delay: GameEvent.sceneOpen
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.popupButtonClick, ({ buttonName, popupName, status, ...additionalParams }) => {
            if (!popupName) {
                popupName = this.modalsManager.getCurrentKey();
            }
            this.sendEventWithTime(GameEvent.popupButtonClick, <GameEventPopupButtonClick>{
                sceneName: this.getSceneName(),
                popupName,
                buttonName,
                delay: GameEvent.popupOpen,
                status: typeof status === 'undefined' ? true : status,
                ...additionalParams
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.tutorialStart, () => {
            this.sendEventWithTime(GameEvent.tutorialStart, {
                delay: GameEvent.sceneOpen,
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.tutorialPopupOpen, ({ popupName }) => {
            this.sendEventWithTime(GameEvent.tutorialPopupOpen, <GameEventTutorialPopupOpen>{
                popupName,
                delay: GameEvent.tutorialStart,
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.dailyReward, ({ popupName, day, type }) => {
            if (!popupName) {
                popupName = this.modalsManager.getCurrentKey();
            }
            this.sendEvent(GameEvent.dailyReward, {
                popupName,
                day,
                type
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.tutorialBoosterUse, ({ boosterName }) => {
            this.sendEventWithTime(GameEvent.tutorialBoosterUse, <GameEventTutorialBoosterUse>{
                boosterName,
                delay: GameEvent.tutorialButtonClick,
            });
        }, this);


        this.modelEvents.on(this.model, GameEvent.tutorialComplete, ({
                                                                         stitchCount,
                                                                         dragCount,
                                                                         zoomInCount,
                                                                         zoomOutCount,
                                                                     }) => {
            this.sendEventWithTime(GameEvent.tutorialComplete, <GameEventTutorialComplete>{
                stitchCount,
                dragCount,
                zoomInCount,
                zoomOutCount,
                delay: GameEvent.tutorialStart,
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.makePurchase, (data) => {
            this.sendEventWithTime(GameEvent.makePurchase, data);
        }, this);

        const onLevelPlay = (ev: GameEvent, data: ILevelPlayData) => {
            const tagType = !!~AnimationContents.CATALOG_TAGS_LIST['hot']?.indexOf(data.levelName) ? 'hot' :
                            !!~AnimationContents.CATALOG_TAGS_LIST['new']?.indexOf(data.levelName) ? 'new' :
                            'none';

            data.tagType = tagType;
            this.sendEventWithTime(ev, data)
        }

        this.modelEvents.on(this.model, GameEvent.levelContinue, (data: ILevelPlayData) => {
            onLevelPlay(GameEvent.levelContinue, data);
        }, this);

        this.modelEvents.on(this.model, GameEvent.levelStart, (data: ILevelPlayData) => {
            onLevelPlay(GameEvent.levelStart, data);
        }, this);

        let lastSendEvent = 0;
        this.modelEvents.on(this.model, GameEvent.levelComplete, ({
                                                                      levelName,
                                                                      levelSize,
                                                                      delay,
                                                                      boosters,
                                                                      stitchCount,
                                                                      dragCount,
                                                                      needleCount,
                                                                      yarnCount,
                                                                      zoomInCount,
                                                                      zoomOutCount,
                                                                      storeCount,
                                                                      timeSpent,
                                                                      rewardsCount
                                                                  }) => {
            // if (levelName === 'FTUE') {
            //     return;
            // }

            if (lastSendEvent > Date.now()) return;
            lastSendEvent = Date.now() + 5000;

            const tagType = !!~AnimationContents.CATALOG_TAGS_LIST['hot']?.indexOf(levelName) ? 'hot' :
                            !!~AnimationContents.CATALOG_TAGS_LIST['new']?.indexOf(levelName) ? 'new' :
                            'none';

            this.sendEventWithTime(GameEvent.levelComplete, {
                levelName,
                levelSize,
                boosters,
                stitchCount,
                dragCount,
                needleCount,
                yarnCount,
                zoomInCount,
                zoomOutCount,
                storeCount,
                rewardsCount,
                timeSpent,
                delay: GameEvent.levelStart,
                tagType,
            });
        }, this);


        this.modelEvents.on(this.model, GameEvent.levelAbort, ({
                                                                   levelName,
                                                                   levelSize,
                                                                   boosters,
                                                                   stitchCount,
                                                                   dragCount,
                                                                   needleCount,
                                                                   yarnCount,
                                                                   zoomInCount,
                                                                   zoomOutCount,
                                                                   storeCount,
                                                                   percentage,
                                                                   timeSpent,
                                                                   rewardsCount
                                                               }) => {

            const tagType = !!~AnimationContents.CATALOG_TAGS_LIST['hot']?.indexOf(levelName) ? 'hot' :
                            !!~AnimationContents.CATALOG_TAGS_LIST['new']?.indexOf(levelName) ? 'new' :
                            'none';

            this.sendEventWithTime(GameEvent.levelAbort, {
                levelName,
                levelSize,
                boosters,
                stitchCount,
                dragCount,
                needleCount,
                yarnCount,
                zoomInCount,
                zoomOutCount,
                storeCount,
                rewardsCount,
                percentage,
                timeSpent,
                delay: GameEvent.levelStart,
                tagType,
            });
        }, this);


        this.modelEvents.on(this.model, GameEvent.boosterUse, ({ boosterName, levelName, levelSize }) => {
            if (levelName === 'FTUE') {
                return;
            }

            this.sendEventWithTime(GameEvent.boosterUse, <GameEventBoosterUse & GameEventDelay>{
                boosterName,
                levelName,
                levelSize,
                delay: GameEvent.sceneButtonClick
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.sceneButtonClick, ({ buttonName, status }) => {
            this.sendEventWithTime(this.isFTUE() ? GameEvent.tutorialButtonClick : GameEvent.sceneButtonClick, {
                sceneName: this.getSceneName(),
                buttonName,
                delay: this.isFTUE() ? GameEvent.tutorialPopupOpen : GameEvent.sceneOpen,
                status,
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.rewardButtonClick, ({ buttonName, status, goal, reactionTime }) => {
            this.sendEventWithTime(this.isFTUE() ? GameEvent.progressBarTutorialButtonClick : GameEvent.rewardButtonClick, {
                sceneName: this.getSceneName(),
                buttonName,
                delay: this.isFTUE() ? GameEvent.progressBarTutorialStart : GameEvent.sceneOpen,
                status,
                goal,
                reactionTime
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.tutorialButtonClick, ({ buttonName, popupName }) => {
            this.sendEventWithTime(GameEvent.tutorialButtonClick, {
                sceneName: this.getSceneName(),
                popupName,
                buttonName,
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.adStart, ({ id, time }) => {
            this.adStartTimeStamp = new Date().getTime();

            const popupName = this.modalsManager.getCurrentKey();
            const sceneName = this.getSceneName();

            const props = <GameEventAdStart & GameEventDelay>{
                id,
                time,
                delay: GameEvent.adStart,
            };

            if (sceneName !== GameEventSceneName.lobby && sceneName !== GameEventSceneName.catalog) {
                if (this.model.picture?.name) {
                    props['levelName'] = this.model.picture.name;
                }
            }

            if (popupName) {
                props.popupName = popupName;
            } else {
                props.sceneName = sceneName;
            }

            this.sendEventWithTime(GameEvent.adStart, props);
        }, this);

        this.modelEvents.on(this.model, GameEvent.adComplete, ({ id, time, skip }) => {
            const popupName = this.modalsManager.getCurrentKey();
            const sceneName = this.getSceneName();

            const props = <GameEventAdComplete & GameEventDelay>{
                id,
                time: new Date().getTime() - this.adStartTimeStamp,
                delay: GameEvent.adStart,
            };

            if (sceneName !== GameEventSceneName.lobby && sceneName !== GameEventSceneName.catalog) {
                if (this.model.picture?.name) {
                    props['levelName'] = this.model.picture.name;
                }
            }

            if (popupName) {
                props.popupName = popupName;
            } else {
                props.sceneName = sceneName;
            }

            this.sendEventWithTime(GameEvent.adComplete, props);

            this.adStartTimeStamp = 0;
        }, this);

        this.modelEvents.on(this.model, GameEvent.interstitialStart, ({ id, time }) => {
            const popupName = this.modalsManager.getCurrentKey();
            const sceneName = this.getSceneName();

            const props = <GameEventAdStart & GameEventDelay>{
                id,
                time,
                delay: GameEvent.adStart,
            };

            if (sceneName !== GameEventSceneName.lobby && sceneName !== GameEventSceneName.catalog) {
                if (this.model.picture?.name) {
                    props['levelName'] = this.model.picture.name;
                }
            }

            if (popupName) {
                props.popupName = popupName;
            } else {
                props.sceneName = sceneName;
            }

            this.sendEventWithTime(GameEvent.interstitialStart, props);
        }, this);

        this.modelEvents.on(this.model, GameEvent.adAbort, ({ id, time, cause, message }) => {
            const popupName = this.modalsManager.getCurrentKey();
            const sceneName = this.getSceneName();

            const props = <GameEventAdAbort & GameEventDelay>{
                id,
                time,
                cause,
                message,
                delay: GameEvent.adStart,
            };

            if (sceneName !== GameEventSceneName.lobby && sceneName !== GameEventSceneName.catalog) {
                if (this.model.picture?.name) {
                    props['levelName'] = this.model.picture.name;
                }
            }

            if (popupName) {
                props.popupName = popupName;
            } else {
                props.sceneName = sceneName;
            }

            this.sendEventWithTime(GameEvent.adAbort, props);
        }, this);


        this.modelEvents.on(this.model, GameEvent.interstitialAbort, ({ id, time, cause, message }) => {
            const popupName = this.modalsManager.getCurrentKey();
            const sceneName = this.getSceneName();

            const props = <GameEventAdAbort & GameEventDelay>{
                id,
                time,
                cause,
                message,
                delay: GameEvent.adStart,
            };

            if (sceneName !== GameEventSceneName.lobby && sceneName !== GameEventSceneName.catalog) {
                if (this.model.picture?.name) {
                    props['levelName'] = this.model.picture.name;
                }
            }

            if (popupName) {
                props.popupName = popupName;
            } else {
                props.sceneName = sceneName;
            }

            this.sendEventWithTime(GameEvent.interstitialAbort, props);
        }, this);

        this.modelEvents.on(this.model, GameEvent.interstitialComplete, ({ id, time, skip }) => {
            const popupName = this.modalsManager.getCurrentKey();
            const sceneName = this.getSceneName();

            const props = <GameEventAdComplete & GameEventDelay>{
                id,
                time,
                skip,
                delay: GameEvent.adStart,
            };

            if (sceneName !== GameEventSceneName.lobby && sceneName !== GameEventSceneName.catalog) {
                if (this.model.picture?.name) {
                    props['levelName'] = this.model.picture.name;
                }
            }

            if (popupName) {
                props.popupName = popupName;
            } else {
                props.sceneName = sceneName;
            }

            this.sendEventWithTime(GameEvent.interstitialComplete, props);
        }, this);

        this.modelEvents.on(this.model, GameEvent.storeOpen, ({ yarnCount, needleCount, triggered, channel }) => {
            this.sendEventWithTime(GameEvent.storeOpen, <GameEventStoreOpen & GameEventDelay>{
                yarnCount,
                needleCount,
                triggered,
                channel,
                delay: GameEvent.levelStart,
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.storeButtonClick, ({ buttonName, status }) => {
            this.sendEventWithTime(GameEvent.storeButtonClick, <GameEventStoreButtonClick & GameEventDelay>{
                buttonName,
                status,
                delay: GameEvent.storeOpen,
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.storeExit, ({ actionsCount, itemsCount, channel }) => {
            this.sendEventWithTime(GameEvent.storeExit, <GameEventStoreExit & GameEventDelay>{
                actionsCount,
                itemsCount,
                channel,
                delay: GameEvent.storeOpen,
            });
        }, this);

        this.modelEvents.on(this.model, GameEvent.scroll, ({popupName}) => {
            this.sendEventWithTime(GameEvent.scroll, {
                sceneName: this.getSceneName(),
                popupName,
                delay: GameEvent.popupOpen
            })
        }, this);

        // dispatch loading start
        this.modelEvents.dispatch(this.model, GameEvent.preloaderStart);

        // TODO: move to another place
        if (!isWebGLSupport()) {
            this.modelEvents.dispatch(this.model, GameEvent.preloaderAbort, { cause: GameEventPreloaderAbortCause.webGL });
        }

        window.addEventListener('beforeunload', this.onBeforeUnload);
    }

    private onBeforeUnload = () => {
        this.modelEvents.dispatch(this.model, GameEvent.preloaderAbort, { cause: GameEventPreloaderAbortCause.forcedExit });
    }

    sendEvent(eventName: GameEvent, eventParams: any = {}) {
        if (window['__DISABLE_ANALYTICS__']) return;
        if ('delay' in eventParams) {
            const timestamp = this.eventStamps.get(eventParams?.delay ?? '') ?? 0;
            eventParams.delay = timestamp === 0 ? 0 : Math.floor((Date.now() - timestamp) / 1000);
        }
        eventParams.lifetime = Number(localStorage.getItem('lifetime'));
        this.eventStamps.set(eventName, Date.now());
        this.amplitudeAnalyticsService.sendEvent(eventName, eventParams);
        this.googleAnalyticsService.sendEvent(eventName, eventParams);
        this.updateDelay();

        console.log('AnalyticsService::sendEvent', eventName, eventParams);
    }

    updateDelay(): void {
        this.lastSendTimeStamp = new Date().getTime();
    }

    getDelay(): number {
        return new Date().getTime() - this.lastSendTimeStamp;
    }

    getSceneName(state = this.model.gameState): GameEventSceneName {
        switch (state) {
            case GameStateEnum.game:
                return GameEventSceneName.gameplay;

            case GameStateEnum.main:
                return GameEventSceneName.lobby;

            case GameStateEnum.win:
                return GameEventSceneName.endScreen;

            case GameStateEnum.catalog:
                return GameEventSceneName.catalog;
        }

        return GameEventSceneName.unknown;
    }

    isFTUE(): boolean {
        return this.saveManager.getSaveGameData().tutorialStep != -1;
    }

    sendEventWithTime(eventName: GameEvent, eventParams: any = {}) {
        eventParams.delay = eventParams.delay ?? this.getDelay();
        this.sendEvent(eventName, eventParams);
    }

    sendEventPage(pageName: string, eventParams: any = {}) {
        this.amplitudeAnalyticsService.sendEventPage(pageName, eventParams);
        this.googleAnalyticsService.sendEventPage(pageName, eventParams);
    }
}
